import React, { useMemo, useState } from 'react';
import { History, Plus } from 'lucide-react';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import Dash from '@dashboard/components/Dash';
import { DataGrid } from '@dashboard/components/DataGrid';
import { useStaffPool, type StaffPoolEntry } from '../hooks/useStaffPool';
import SectionDisclosure from './SectionDisclosure';
import TicketTypeRestriction, { type TicketTypeOption } from './TicketTypeRestriction';

interface Props {
  sellerId: string;
  pageId: string;
  ticketTypeOptions: TicketTypeOption[];
  onAdd: (entries: Array<{ email: string; name?: string; allowedTicketTypes: string[] }>) => void;
  disabled?: boolean;
}

const PastEventsDisclosure: React.FC<Props> = ({
  sellerId,
  pageId,
  ticketTypeOptions,
  onAdd,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const { pool, isLoading } = useStaffPool(sellerId, pageId);
  // Selection keyed by userId via DataGrid's getRowId.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // Per-row picker state. Defaults to empty (= unrestricted) until the
  // merchant edits a row's picker.
  const [rowPermissions, setRowPermissions] = useState<Record<string, string[]>>({});

  const setPermissionsFor = (userId: string, next: string[]) =>
    setRowPermissions(prev => ({ ...prev, [userId]: next }));

  const columns = useMemo<ColumnDef<StaffPoolEntry, unknown>[]>(
    () => [
      {
        id: 'staffer',
        header: 'Staff',
        accessorFn: row => `${row.name ?? ''} ${row.email}`,
        cell: ({ row }) => (
          <div className="leading-tight">
            <div className="font-medium">{row.original.name || row.original.email}</div>
            {row.original.name && (
              <div className="text-xs text-muted-foreground">{row.original.email}</div>
            )}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'lastEventTitle',
        header: 'Last event',
        cell: ({ row }) =>
          row.original.lastEventTitle ? (
            <span className="text-sm text-muted-foreground">{row.original.lastEventTitle}</span>
          ) : (
            <Dash />
          ),
        enableSorting: false,
      },
      {
        id: 'permissions',
        header: 'Ticket scanning',
        cell: ({ row }) => (
          <TicketTypeRestriction
            options={ticketTypeOptions}
            value={rowPermissions[row.original.userId] ?? []}
            onChange={next => setPermissionsFor(row.original.userId, next)}
            disabled={disabled}
          />
        ),
        enableSorting: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticketTypeOptions, rowPermissions],
  );

  const handleAddSelected = () => {
    const picked = Object.entries(rowSelection)
      .filter(([, on]) => on)
      .map(([userId]) => userId);
    if (picked.length === 0) return;
    const userMap = new Map(pool.map(p => [p.userId, p]));
    const fresh: Array<{ email: string; name?: string; allowedTicketTypes: string[] }> = [];
    for (const id of picked) {
      const p = userMap.get(id);
      if (!p) continue;
      fresh.push({
        email: p.email,
        name: p.name,
        allowedTicketTypes: rowPermissions[id] ?? [],
      });
    }
    onAdd(fresh);
    setRowSelection({});
    setOpen(false);
  };

  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  return (
    <SectionDisclosure
      icon={History}
      label="Add from other events"
      badge={
        pool && pool.length > 0 ? (
          <Badge variant="info" className="!py-0 !text-[0.65rem]">
            {`${pool.length} available`}
          </Badge>
        ) : undefined
      }
      open={open}
      onOpenChange={setOpen}
    >
      <DataGrid<StaffPoolEntry>
        columns={columns}
        data={pool}
        isLoading={isLoading}
        enableRowSelection
        getRowId={row => row.userId}
        rowSelection={rowSelection}
        onRowSelectionChange={updater => {
          const next = typeof updater === 'function' ? updater(rowSelection) : updater;
          setRowSelection(next);
        }}
        toolbar={{ showSearch: true, searchPlaceholder: 'Search name or email…' }}
        emptyMessage="No previous staff yet."
      />
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddSelected}
          disabled={disabled || selectedCount === 0}
        >
          <Plus className="size-3.5 mr-1" />
          Add to staff
        </Button>
      </div>
    </SectionDisclosure>
  );
};

export default PastEventsDisclosure;
